/**
 * Engineering page (ADR-0109, ADR-0110) — the GitHub-Issues-backed component.
 *
 * A working surface onto the open lf2.0 issues in chrisfnrose/LegalFlare-Engineering,
 * not just a window. Each row is one issue: #number, title (links out to GitHub),
 * its labels, assignees, comment count, and when it was last updated — plus the
 * v1 write bicycle (ADR-0110):
 *
 *   - "+ New issue" files an issue here instead of on the operator Dashboard
 *     (the inflow capture);
 *   - Pin / Unpin toggles the `surfaced` label — pinned issues float to a group
 *     at the top (manual float: the operator decides what's hot);
 *   - Close marks an issue done (it drops off the open list), behind an inline
 *     confirm so a misclick can't close an issue.
 *
 * Non-list states:
 *   - loading / error (a genuine GitHub failure → retry copy);
 *   - not connected — the worker has no GITHUB_TOKEN yet (`configured: false`),
 *     so we show a connect-the-tracker panel instead of a red error;
 *   - connected but empty — no open lf2.0 issues, a clean "all clear".
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  useCloseIssue,
  useEngineering,
  useSetPinned,
} from "@/lib/use-engineering";
import { ENGINEERING_PINNED_LABEL } from "@command-center/shared";
import type { EngineeringIssue } from "@command-center/shared";
import { CreateIssueModal } from "./create-issue-modal";

export function EngineeringPage() {
  const query = useEngineering();
  return (
    <section className="mx-auto max-w-4xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">Engineering</h1>
      <p className="mb-5 text-sm text-plane-muted">
        Open issues on the engineering tracker.
      </p>

      {query.isLoading ? (
        <div
          className="p-8 text-sm text-plane-muted"
          data-testid="engineering-loading"
        >
          Loading…
        </div>
      ) : query.isError ? (
        <div
          className="p-8 text-sm text-error"
          data-testid="engineering-error"
        >
          Couldn't load Engineering. It will retry automatically.
        </div>
      ) : query.data ? (
        query.data.configured ? (
          <ConnectedView
            repo={query.data.repo}
            label={query.data.label}
            issues={query.data.issues}
          />
        ) : (
          <NotConnected repo={query.data.repo} />
        )
      ) : null}
    </section>
  );
}

function isPinned(issue: EngineeringIssue): boolean {
  return issue.labels.includes(ENGINEERING_PINNED_LABEL);
}

function ConnectedView({
  repo,
  label,
  issues,
}: {
  repo: string;
  label: string;
  issues: EngineeringIssue[];
}) {
  const [creating, setCreating] = useState(false);

  // Manual float (ADR-0110): pinned issues sort to a group at the top; each
  // group keeps the server's newest-updated-first order.
  const pinned = issues.filter(isPinned);
  const rest = issues.filter((i) => !isPinned(i));

  return (
    <div data-testid="engineering">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div className="text-sm text-plane-muted">
          {issues.length} open {issues.length === 1 ? "issue" : "issues"} ·{" "}
          <span className="font-medium text-plane-foreground">{label}</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <Button
            size="sm"
            onClick={() => setCreating(true)}
            data-testid="engineering-new-issue"
          >
            + New issue
          </Button>
          <a
            className="text-xs text-plane-muted underline hover:text-plane-foreground"
            href={`https://github.com/${repo}/issues`}
            target="_blank"
            rel="noreferrer"
          >
            {repo} ↗
          </a>
        </div>
      </div>

      {issues.length === 0 ? (
        <div
          className="p-8 text-sm text-plane-muted"
          data-testid="engineering-empty"
        >
          No open {label} issues. All clear.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pinned.length > 0 ? (
            <div data-testid="engineering-pinned">
              <h2 className="mb-2 text-xs font-sans font-medium uppercase tracking-wider text-plane-muted">
                📌 Pinned
              </h2>
              <ul className="flex flex-col gap-2">
                {pinned.map((issue) => (
                  <IssueRow key={issue.number} issue={issue} pinned />
                ))}
              </ul>
            </div>
          ) : null}

          {rest.length > 0 ? (
            <div>
              {pinned.length > 0 ? (
                <h2 className="mb-2 text-xs font-sans font-medium uppercase tracking-wider text-plane-muted">
                  Open
                </h2>
              ) : null}
              <ul className="flex flex-col gap-2" data-testid="engineering-list">
                {rest.map((issue) => (
                  <IssueRow key={issue.number} issue={issue} pinned={false} />
                ))}
              </ul>
            </div>
          ) : (
            // Every open issue is pinned — keep the list testid present so the
            // connected-with-issues state is always addressable.
            <ul className="hidden" data-testid="engineering-list" />
          )}
        </div>
      )}

      <CreateIssueModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function IssueRow({
  issue,
  pinned,
}: {
  issue: EngineeringIssue;
  pinned: boolean;
}) {
  const setPinned = useSetPinned();
  const closeIssue = useCloseIssue();
  const [confirmingClose, setConfirmingClose] = useState(false);

  // The pin label is represented by the pin control + the group, not a chip.
  const chips = issue.labels.filter((l) => l !== ENGINEERING_PINNED_LABEL);
  const busy = setPinned.isPending || closeIssue.isPending;

  return (
    <li>
      <Card className="p-3" data-testid={`engineering-issue-${issue.number}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <a
              className="text-sm font-medium leading-snug hover:underline"
              href={issue.url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="text-muted-foreground">#{issue.number}</span>{" "}
              {issue.title}
            </a>
            {chips.length > 0 ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {chips.map((name) => (
                  <Badge key={name} variant="slate">
                    {name}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground">
            <span>updated {fmtUpdated(issue.updatedAt)}</span>
            <span>
              {issue.assignees.length > 0 ? (
                issue.assignees.join(", ")
              ) : (
                <span className="italic">unassigned</span>
              )}
              {issue.comments > 0
                ? ` · ${issue.comments} comment${issue.comments === 1 ? "" : "s"}`
                : null}
            </span>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              setPinned.mutate({ number: issue.number, pinned: !pinned })
            }
            data-testid={`engineering-pin-${issue.number}`}
          >
            {pinned ? "Unpin" : "Pin"}
          </Button>

          {confirmingClose ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirmingClose(false)}
                data-testid={`engineering-close-cancel-${issue.number}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => closeIssue.mutate(issue.number)}
                data-testid={`engineering-close-confirm-${issue.number}`}
              >
                {closeIssue.isPending ? "Closing…" : "Confirm close"}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setConfirmingClose(true)}
              data-testid={`engineering-close-${issue.number}`}
            >
              Close
            </Button>
          )}
        </div>
      </Card>
    </li>
  );
}

function NotConnected({ repo }: { repo: string }) {
  return (
    <Card className="p-6" data-testid="engineering-not-connected">
      <h2 className="text-base font-sans font-semibold">Tracker not connected</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Engineering reads from and writes to the{" "}
        <span className="font-medium text-foreground">{repo}</span> issue
        tracker. Add a{" "}
        <code className="rounded bg-muted px-1 py-0.5 text-xs">GITHUB_TOKEN</code>{" "}
        secret to the Command Center worker (a fine-grained PAT with Issues:
        read and write) to surface, create, and close issues here.
      </p>
      <a
        className="mt-3 inline-block text-sm underline hover:text-foreground"
        href={`https://github.com/${repo}/issues`}
        target="_blank"
        rel="noreferrer"
      >
        Open the tracker on GitHub ↗
      </a>
    </Card>
  );
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "Jun 12" from an ISO timestamp. UTC-anchored so rendering is deterministic
 *  (no Date.now / local-tz drift) — the test pins exact labels off this. */
function fmtUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTH_NAMES[d.getUTCMonth()] ?? ""} ${d.getUTCDate()}`;
}
